/* eslint-disable max-len */

/**
 * @author Andrew Gower <amogower@gmail.com>
 */

/**
 * APIResponse Class
 * @constructor
 * @param {Object} config - The store config object
 */
export default class APIResponse {
    constructor(response, addRelatedIdsToMainObject = false) {
        if (!response) {
            throw new Error('APIResponse->constructor: response - ', response);
        }
        const { data, meta } = response.data;
        let relationships;
        if (data instanceof Array) {
            this.body = this.mungeEachObject(response, addRelatedIdsToMainObject);
        } else if (data instanceof Object) {
            this.body = this.mungeMainObject(data);
            relationships = response.data.data.relationships;
        }
        if (relationships) {
            this.relationships = this.handleRelationships(response);
            if (addRelatedIdsToMainObject) {
                Object.keys(this.relationships).forEach(relation => {
                    this.body[`${relation}Id`] = this.relationships[relation].id;
                });
            }
        }
        if (meta && meta.refId) {
            this.body.refId = meta.refId;
        }
    }

    /**
     * Munge the included data from the response
     * @param {Object} related - The related data object
     * @param {Array} included - The response included data array
     * @return {Object} The formatted included data
     */
    getIncludedDataByTypeFromArray(related, included) {
        if (!related) {
            throw new Error(`APIResponse->getIncludedDataByTypeFromArray: related - ${related}`);
        }
        if (!included) {
            throw new Error(`APIResponse->getIncludedDataByTypeFromArray: included - ${included}`);
        }
        return related.map(relation => {
            const includedData = included.find(include =>
                include.type === relation.type && include.id === relation.id
            );
            const retObj = {
                ...includedData.attributes,
                id: relation.id,
                type: relation.type
            };
            if (includedData.relationships) {
                const relationships = this.mungeRelationships(includedData.relationships, included);
                Object.keys(relationships).forEach(key => {
                    if (key === 'images' || key === 'assetTags') {
                        retObj[key] = relationships[key];
                    }
                });
                const relatedIdObj = this.relationshipIdCreator(includedData.relationships, included);
                if (Object.keys(relatedIdObj).length > 0) {
                    Object.keys(relatedIdObj).forEach(key => {
                        retObj[key] = relatedIdObj[key];
                    });
                }
            }
            return retObj;
        });
    }

    /**
     * Munge the included data from the response
     * @param {Object} related - The related data object
     * @param {Array} included - The response included data array
     * @param {Object} relationship - The data relationship
     * @return {Object} The formatted included data
     */
    getIncludedDataByTypeFromObject(related, included, relationship) {
        if (!related) {
            throw new Error(`APIResponse->getIncludedDataByTypeFromObject: related - ${related}`);
        }
        if (!included) {
            throw new Error(`APIResponse->getIncludedDataByTypeFromObject: included - ${included}`);
        }
        if (!relationship) {
            throw new Error(
                `APIResponse->getIncludedDataByTypeFromObject: relationship - ${relationship}`
            );
        }
        const includedData = included.find(include =>
            include.type === related.type && include.id === related.id
        );
        const retObj = {
            ...includedData.attributes,
            id: related.id,
            type: related.type
        };
        if (includedData.relationships) {
            const relatedIdObj = this.relationshipIdCreator(includedData.relationships, included);
            if (Object.keys(relatedIdObj).length > 0) {
                Object.keys(relatedIdObj).forEach(key => {
                    retObj[key] = relatedIdObj[key];
                });
            }
        }
        return retObj;
    }

    /**
     * Munge the main data from the response
     * @param {Object} data - The main data object
     * @return {Object} The formatted object
     */
    mungeMainObject({ attributes, id, type }) {
        if (!id) {
            throw new Error(`APIResponse->mungeMainObject: id - ${id}`);
        }
        if (!type) {
            throw new Error(`APIResponse->mungeMainObject: type - ${type}`);
        }
        let retObj = {
            id,
            type
        };
        if (attributes) {
            retObj = {
                ...attributes,
                id,
                type
            };
        }
        return retObj;
    }

    /**
     * Munge each object in the response data array
     * @param {Array} response - The main data array
     * @param {Bool} addRelatedIdsToMainObject - Should add related ids to each object
     * @return {Array} Array of formatted objects
     */
    mungeEachObject(response, addRelatedIdsToMainObject) {
        if (!response) {
            throw new Error(`APIResponse->mungeEachObject: response - ${response}`);
        }
        return response.data.data.map(({ attributes, id, relationships, type }) => {
            const retObj = {
                ...attributes,
                id,
                type
            };
            if (relationships) {
                const mungedRelationships = this.mungeRelationships(relationships, response.data.included);
                if (addRelatedIdsToMainObject) {
                    Object.keys(mungedRelationships).forEach(relation => {
                        retObj[`${relation}Id`] = mungedRelationships[relation].id;
                    });
                }
                if (relationships.images) {
                    retObj.images = mungedRelationships.images;
                }
            }
            return retObj;
        });
    }

    /**
     * Handle the related data from the response
     * @param {Object} response - The response object
     * @return {Object} The formatted relationships object
     */
    handleRelationships(response) {
        if (!response) {
            throw new Error(`APIResponse->mungeRelationships: response - ${response}`);
        }
        const { included } = response.data;
        const { relationships } = response.data.data;
        return this.mungeRelationships(relationships, included);
    }

    /**
     * Munge the related data from the response
     * @param {Object} relationships - The relationships object
     * @param {Array} included - The included array
     * @return {Object} The formatted relationships object
     */
    mungeRelationships(relationships, included) {
        const relationshipsObj = {};
        if (relationships && Object.keys(relationships).length > 0 && included && included.length > 0) {
            Object.keys(relationships).forEach(relationship => {
                const related = relationships[relationship];
                if (related.data instanceof Array) {
                    if (related.data.length > 0) {
                        relationshipsObj[relationship] = this.getIncludedDataByTypeFromArray(related.data, included);
                    }
                } else if (related.data && related.data.length > 0) {
                    relationshipsObj[relationship] = this.getIncludedDataByTypeFromObject(related.data, included, relationship);
                }
            });
        }
        return relationshipsObj;
    }

    relationshipIdCreator(relationships, included) {
        const retObj = {};
        if (relationships && Object.keys(relationships).length > 0 && included && included.length > 0) {
            Object.keys(relationships).forEach(relationship => {
                const includedRelated = relationships[relationship];
                let includedRelatedData;
                if (includedRelated.data instanceof Array) {
                    if (includedRelated.data.length > 0) {
                        includedRelatedData = this.getIncludedDataByTypeFromArray(includedRelated.data, included);
                    }
                } else if (includedRelated.data && includedRelated.data.length > 0) {
                    includedRelatedData = this.getIncludedDataByTypeFromObject(includedRelated.data, included, relationship);
                }
                if (typeof includedRelatedData !== 'undefined' && relationship !== 'images' && relationship !== 'assetTags') {
                    retObj[`${relationship}Id`] = includedRelatedData.id;
                }
            });
        }
        return retObj;
    }
}



// WEBPACK FOOTER //
// ./src/API/APIResponse/APIResponse.js